import type { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Field, Fields, type IFIXParser, MDEntryType, type Message, Messages } from 'fixparser';
import QuickChart from 'quickchart-js';
import type { MarketDataEntry } from '../schemas';

export const createMarketDataRequestHandler = (
    parser: IFIXParser,
    pendingRequests: Map<string, (data: Message) => void>,
): ((args: {
    mdUpdateType: string;
    symbols: string[];
    mdReqID: string;
    subscriptionRequestType: string;
    mdEntryTypes?: string[];
}) => Promise<CallToolResult>) => {
    return async (args: {
        mdUpdateType: string;
        symbols: string[];
        mdReqID: string;
        subscriptionRequestType: string;
        mdEntryTypes?: string[];
    }): Promise<CallToolResult> => {
        try {
            parser.logger.log({
                level: 'info',
                message: `Sending market data request for symbols: ${args.symbols.join(', ')}`,
            });

            const response = new Promise((resolve) => {
                pendingRequests.set(args.mdReqID, resolve);
                parser.logger.log({
                    level: 'info',
                    message: `Registered callback for market data request ID: ${args.mdReqID}`,
                });
            });

            // Set default entry types if not provided
            const entryTypes = args.mdEntryTypes || [
                MDEntryType.Bid,
                MDEntryType.Offer,
                MDEntryType.Trade,
                MDEntryType.IndexValue,
                MDEntryType.OpeningPrice,
                MDEntryType.ClosingPrice,
                MDEntryType.SettlementPrice,
                MDEntryType.TradingSessionHighPrice,
                MDEntryType.TradingSessionLowPrice,
                MDEntryType.VWAP,
                MDEntryType.Imbalance,
                MDEntryType.TradeVolume,
                MDEntryType.OpenInterest,
                MDEntryType.CompositeUnderlyingPrice,
                MDEntryType.SimulatedSellPrice,
                MDEntryType.SimulatedBuyPrice,
                MDEntryType.MarginRate,
                MDEntryType.MidPrice,
                MDEntryType.EmptyBook,
                MDEntryType.SettleHighPrice,
                MDEntryType.SettleLowPrice,
                MDEntryType.PriorSettlePrice,
                MDEntryType.SessionHighBid,
                MDEntryType.SessionLowOffer,
                MDEntryType.EarlyPrices,
                MDEntryType.AuctionClearingPrice,
                MDEntryType.SwapValueFactor,
                MDEntryType.DailyValueAdjustmentForLongPositions,
                MDEntryType.CumulativeValueAdjustmentForLongPositions,
                MDEntryType.DailyValueAdjustmentForShortPositions,
                MDEntryType.CumulativeValueAdjustmentForShortPositions,
                MDEntryType.FixingPrice,
                MDEntryType.CashRate,
                MDEntryType.RecoveryRate,
                MDEntryType.RecoveryRateForLong,
                MDEntryType.RecoveryRateForShort,
                MDEntryType.MarketBid,
                MDEntryType.MarketOffer,
                MDEntryType.ShortSaleMinPrice,
                MDEntryType.PreviousClosingPrice,
                MDEntryType.ThresholdLimitPriceBanding,
                MDEntryType.DailyFinancingValue,
                MDEntryType.AccruedFinancingValue,
                MDEntryType.TWAP,
            ];

            const messageFields = [
                new Field(Fields.MsgType, Messages.MarketDataRequest),
                new Field(Fields.SenderCompID, parser.sender),
                new Field(Fields.MsgSeqNum, parser.getNextTargetMsgSeqNum()),
                new Field(Fields.TargetCompID, parser.target),
                new Field(Fields.SendingTime, parser.getTimestamp()),
                new Field(Fields.MDReqID, args.mdReqID),
                new Field(Fields.SubscriptionRequestType, args.subscriptionRequestType),
                new Field(Fields.MarketDepth, 0),
                new Field(Fields.MDUpdateType, args.mdUpdateType),
            ];

            // Add each symbol to the message
            messageFields.push(new Field(Fields.NoRelatedSym, args.symbols.length));
            args.symbols.forEach((symbol: string) => {
                messageFields.push(new Field(Fields.Symbol, symbol));
            });

            // Add NoMDEntryTypes and each MDEntryType to the message
            messageFields.push(new Field(Fields.NoMDEntryTypes, entryTypes.length));
            entryTypes.forEach((entryType: string) => {
                messageFields.push(new Field(Fields.MDEntryType, entryType));
            });

            const mdr = parser.createMessage(...messageFields);

            if (!parser.connected) {
                parser.logger.log({
                    level: 'error',
                    message: 'Not connected. Cannot send market data request.',
                });
                return {
                    content: [
                        {
                            type: 'text',
                            text: 'Error: Not connected. Ignoring message.',
                            uri: 'marketDataRequest',
                        },
                    ],
                    isError: true,
                };
            }

            parser.logger.log({
                level: 'info',
                message: `Sending market data request message: ${JSON.stringify(mdr?.toFIXJSON())}`,
            });

            parser.send(mdr!);

            // Wait for the market data response
            const fixData = await response;

            parser.logger.log({
                level: 'info',
                message: `Received market data response for request ID: ${args.mdReqID}`,
            });

            return {
                content: [
                    {
                        type: 'text',
                        text: `Market data for ${args.symbols.join(', ')}: ${JSON.stringify((fixData as Message).toFIXJSON())}`,
                        uri: 'marketDataRequest',
                    },
                ],
            };
        } catch (error) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: ${error instanceof Error ? error.message : 'Failed to request market data'}`,
                        uri: 'marketDataRequest',
                    },
                ],
                isError: true,
            };
        }
    };
};

/**
 * Aggregates market data points to ensure maximum of 500 data points for charting
 * Uses smart sampling to preserve important characteristics like highs, lows, and trends
 */
const aggregateMarketData = (priceHistory: MarketDataEntry[], maxPoints = 240): MarketDataEntry[] => {
    if (priceHistory.length <= maxPoints) {
        return priceHistory;
    }

    const result: MarketDataEntry[] = [];
    const step = priceHistory.length / maxPoints;

    // Always include the first and last points
    result.push(priceHistory[0]);

    // Sample points with smart aggregation
    for (let i = 1; i < maxPoints - 1; i++) {
        const startIndex = Math.floor(i * step);
        const endIndex = Math.floor((i + 1) * step);
        const segment = priceHistory.slice(startIndex, endIndex);

        if (segment.length === 0) continue;

        // Aggregate the segment by taking weighted averages
        const aggregatedPoint: MarketDataEntry = {
            timestamp: segment[0].timestamp, // Use timestamp of first point in segment
            bid: segment.reduce((sum, p) => sum + p.bid, 0) / segment.length,
            offer: segment.reduce((sum, p) => sum + p.offer, 0) / segment.length,
            spread: segment.reduce((sum, p) => sum + p.spread, 0) / segment.length,
            volume: segment.reduce((sum, p) => sum + p.volume, 0) / segment.length,
            trade: segment.reduce((sum, p) => sum + p.trade, 0) / segment.length,
            indexValue: segment.reduce((sum, p) => sum + p.indexValue, 0) / segment.length,
            openingPrice: segment.reduce((sum, p) => sum + p.openingPrice, 0) / segment.length,
            closingPrice: segment.reduce((sum, p) => sum + p.closingPrice, 0) / segment.length,
            settlementPrice: segment.reduce((sum, p) => sum + p.settlementPrice, 0) / segment.length,
            tradingSessionHighPrice: segment.reduce((sum, p) => sum + p.tradingSessionHighPrice, 0) / segment.length,
            tradingSessionLowPrice: segment.reduce((sum, p) => sum + p.tradingSessionLowPrice, 0) / segment.length,
            vwap: segment.reduce((sum, p) => sum + p.vwap, 0) / segment.length,
            imbalance: segment.reduce((sum, p) => sum + p.imbalance, 0) / segment.length,
            openInterest: segment.reduce((sum, p) => sum + p.openInterest, 0) / segment.length,
            compositeUnderlyingPrice: segment.reduce((sum, p) => sum + p.compositeUnderlyingPrice, 0) / segment.length,
            simulatedSellPrice: segment.reduce((sum, p) => sum + p.simulatedSellPrice, 0) / segment.length,
            simulatedBuyPrice: segment.reduce((sum, p) => sum + p.simulatedBuyPrice, 0) / segment.length,
            marginRate: segment.reduce((sum, p) => sum + p.marginRate, 0) / segment.length,
            midPrice: segment.reduce((sum, p) => sum + p.midPrice, 0) / segment.length,
            emptyBook: segment.reduce((sum, p) => sum + p.emptyBook, 0) / segment.length,
            settleHighPrice: segment.reduce((sum, p) => sum + p.settleHighPrice, 0) / segment.length,
            settleLowPrice: segment.reduce((sum, p) => sum + p.settleLowPrice, 0) / segment.length,
            priorSettlePrice: segment.reduce((sum, p) => sum + p.priorSettlePrice, 0) / segment.length,
            sessionHighBid: segment.reduce((sum, p) => sum + p.sessionHighBid, 0) / segment.length,
            sessionLowOffer: segment.reduce((sum, p) => sum + p.sessionLowOffer, 0) / segment.length,
            earlyPrices: segment.reduce((sum, p) => sum + p.earlyPrices, 0) / segment.length,
            auctionClearingPrice: segment.reduce((sum, p) => sum + p.auctionClearingPrice, 0) / segment.length,
            swapValueFactor: segment.reduce((sum, p) => sum + p.swapValueFactor, 0) / segment.length,
            dailyValueAdjustmentForLongPositions:
                segment.reduce((sum, p) => sum + p.dailyValueAdjustmentForLongPositions, 0) / segment.length,
            cumulativeValueAdjustmentForLongPositions:
                segment.reduce((sum, p) => sum + p.cumulativeValueAdjustmentForLongPositions, 0) / segment.length,
            dailyValueAdjustmentForShortPositions:
                segment.reduce((sum, p) => sum + p.dailyValueAdjustmentForShortPositions, 0) / segment.length,
            cumulativeValueAdjustmentForShortPositions:
                segment.reduce((sum, p) => sum + p.cumulativeValueAdjustmentForShortPositions, 0) / segment.length,
            fixingPrice: segment.reduce((sum, p) => sum + p.fixingPrice, 0) / segment.length,
            cashRate: segment.reduce((sum, p) => sum + p.cashRate, 0) / segment.length,
            recoveryRate: segment.reduce((sum, p) => sum + p.recoveryRate, 0) / segment.length,
            recoveryRateForLong: segment.reduce((sum, p) => sum + p.recoveryRateForLong, 0) / segment.length,
            recoveryRateForShort: segment.reduce((sum, p) => sum + p.recoveryRateForShort, 0) / segment.length,
            marketBid: segment.reduce((sum, p) => sum + p.marketBid, 0) / segment.length,
            marketOffer: segment.reduce((sum, p) => sum + p.marketOffer, 0) / segment.length,
            shortSaleMinPrice: segment.reduce((sum, p) => sum + p.shortSaleMinPrice, 0) / segment.length,
            previousClosingPrice: segment.reduce((sum, p) => sum + p.previousClosingPrice, 0) / segment.length,
            thresholdLimitPriceBanding:
                segment.reduce((sum, p) => sum + p.thresholdLimitPriceBanding, 0) / segment.length,
            dailyFinancingValue: segment.reduce((sum, p) => sum + p.dailyFinancingValue, 0) / segment.length,
            accruedFinancingValue: segment.reduce((sum, p) => sum + p.accruedFinancingValue, 0) / segment.length,
            twap: segment.reduce((sum, p) => sum + p.twap, 0) / segment.length,
        };

        result.push(aggregatedPoint);
    }

    // Always include the last point
    result.push(priceHistory[priceHistory.length - 1]);

    return result;
};

export const createGetStockGraphHandler = (
    marketDataPrices: Map<string, MarketDataEntry[]>,
): ((args: { symbol: string }) => Promise<CallToolResult>) => {
    return async (args: { symbol: string }): Promise<CallToolResult> => {
        try {
            const symbol = args.symbol;
            const priceHistory = marketDataPrices.get(symbol) || [];

            if (priceHistory.length === 0) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `No price data available for ${symbol}`,
                            uri: 'getStockGraph',
                        },
                    ],
                };
            }

            // Aggregate data to ensure maximum 500 data points
            const aggregatedData = aggregateMarketData(priceHistory, 500);

            const chart = new QuickChart();
            chart.setWidth(1200);
            chart.setHeight(600);
            chart.setBackgroundColor('transparent');

            // Prepare the data using aggregated data
            const labels = aggregatedData.map((point) => new Date(point.timestamp).toLocaleTimeString());
            const bidData = aggregatedData.map((point) => point.bid);
            const offerData = aggregatedData.map((point) => point.offer);
            const spreadData = aggregatedData.map((point) => point.spread);
            const volumeData = aggregatedData.map((point) => point.volume);
            const tradeData = aggregatedData.map((point) => point.trade);
            const vwapData = aggregatedData.map((point) => point.vwap);
            const twapData = aggregatedData.map((point) => point.twap);

            // Normalize volume data to fit on the same scale as price data
            const maxVolume = Math.max(...volumeData.filter((v) => v > 0));
            const maxPrice = Math.max(...bidData, ...offerData, ...tradeData, ...vwapData, ...twapData);
            const normalizedVolumeData = volumeData.map((v) => (v / maxVolume) * maxPrice * 0.3); // Scale to 30% of max price

            const config = {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Bid',
                            data: bidData,
                            borderColor: '#28a745',
                            backgroundColor: 'rgba(40, 167, 69, 0.1)',
                            fill: false,
                            tension: 0.4,
                        },
                        {
                            label: 'Offer',
                            data: offerData,
                            borderColor: '#dc3545',
                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                            fill: false,
                            tension: 0.4,
                        },
                        {
                            label: 'Spread',
                            data: spreadData,
                            borderColor: '#6c757d',
                            backgroundColor: 'rgba(108, 117, 125, 0.1)',
                            fill: false,
                            tension: 0.4,
                        },
                        {
                            label: 'Trade',
                            data: tradeData,
                            borderColor: '#ffc107',
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            fill: false,
                            tension: 0.4,
                        },
                        {
                            label: 'VWAP',
                            data: vwapData,
                            borderColor: '#17a2b8',
                            backgroundColor: 'rgba(23, 162, 184, 0.1)',
                            fill: false,
                            tension: 0.4,
                        },
                        {
                            label: 'TWAP',
                            data: twapData,
                            borderColor: '#6610f2',
                            backgroundColor: 'rgba(102, 16, 242, 0.1)',
                            fill: false,
                            tension: 0.4,
                        },
                        {
                            label: 'Volume (Normalized)',
                            data: normalizedVolumeData,
                            borderColor: '#007bff',
                            backgroundColor: 'rgba(0, 123, 255, 0.1)',
                            fill: true,
                            tension: 0.4,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    plugins: {
                        title: {
                            display: true,
                            text: `${symbol} Market Data (Volume normalized to 30% of max price)`,
                        },
                    },
                    scales: {
                        y: {
                            beginAtZero: false,
                            title: {
                                display: true,
                                text: 'Price / Normalized Volume',
                            },
                        },
                    },
                },
            };

            chart.setConfig(config);

            const imageBuffer = await chart.toBinary();
            const base64 = imageBuffer.toString('base64');

            return {
                content: [
                    {
                        type: 'resource',
                        resource: {
                            uri: 'resource://graph',
                            mimeType: 'image/png',
                            blob: base64,
                        },
                    },
                ],
            };
        } catch (error) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: ${error instanceof Error ? error.message : 'Failed to generate graph'}`,
                        uri: 'getStockGraph',
                    },
                ],
                isError: true,
            };
        }
    };
};

export const createGetStockPriceHistoryHandler = (
    marketDataPrices: Map<string, MarketDataEntry[]>,
): ((args: { symbol: string }) => Promise<CallToolResult>) => {
    return async (args: { symbol: string }): Promise<CallToolResult> => {
        try {
            const symbol = args.symbol;
            const priceHistory = marketDataPrices.get(symbol) || [];

            if (priceHistory.length === 0) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `No price data available for ${symbol}`,
                            uri: 'getStockPriceHistory',
                        },
                    ],
                };
            }

            // Aggregate data to ensure maximum 500 data points for consistency with graph function
            const aggregatedData = aggregateMarketData(priceHistory, 500);

            return {
                content: [
                    {
                        type: 'text',
                        text: JSON.stringify(
                            {
                                symbol,
                                count: aggregatedData.length,
                                originalCount: priceHistory.length,
                                data: aggregatedData.map((point) => ({
                                    timestamp: new Date(point.timestamp).toISOString(),
                                    bid: point.bid,
                                    offer: point.offer,
                                    spread: point.spread,
                                    volume: point.volume,
                                    trade: point.trade,
                                    indexValue: point.indexValue,
                                    openingPrice: point.openingPrice,
                                    closingPrice: point.closingPrice,
                                    settlementPrice: point.settlementPrice,
                                    tradingSessionHighPrice: point.tradingSessionHighPrice,
                                    tradingSessionLowPrice: point.tradingSessionLowPrice,
                                    vwap: point.vwap,
                                    imbalance: point.imbalance,
                                    openInterest: point.openInterest,
                                    compositeUnderlyingPrice: point.compositeUnderlyingPrice,
                                    simulatedSellPrice: point.simulatedSellPrice,
                                    simulatedBuyPrice: point.simulatedBuyPrice,
                                    marginRate: point.marginRate,
                                    midPrice: point.midPrice,
                                    emptyBook: point.emptyBook,
                                    settleHighPrice: point.settleHighPrice,
                                    settleLowPrice: point.settleLowPrice,
                                    priorSettlePrice: point.priorSettlePrice,
                                    sessionHighBid: point.sessionHighBid,
                                    sessionLowOffer: point.sessionLowOffer,
                                    earlyPrices: point.earlyPrices,
                                    auctionClearingPrice: point.auctionClearingPrice,
                                    swapValueFactor: point.swapValueFactor,
                                    dailyValueAdjustmentForLongPositions: point.dailyValueAdjustmentForLongPositions,
                                    cumulativeValueAdjustmentForLongPositions:
                                        point.cumulativeValueAdjustmentForLongPositions,
                                    dailyValueAdjustmentForShortPositions: point.dailyValueAdjustmentForShortPositions,
                                    cumulativeValueAdjustmentForShortPositions:
                                        point.cumulativeValueAdjustmentForShortPositions,
                                    fixingPrice: point.fixingPrice,
                                    cashRate: point.cashRate,
                                    recoveryRate: point.recoveryRate,
                                    recoveryRateForLong: point.recoveryRateForLong,
                                    recoveryRateForShort: point.recoveryRateForShort,
                                    marketBid: point.marketBid,
                                    marketOffer: point.marketOffer,
                                    shortSaleMinPrice: point.shortSaleMinPrice,
                                    previousClosingPrice: point.previousClosingPrice,
                                    thresholdLimitPriceBanding: point.thresholdLimitPriceBanding,
                                    dailyFinancingValue: point.dailyFinancingValue,
                                    accruedFinancingValue: point.accruedFinancingValue,
                                    twap: point.twap,
                                })),
                            },
                            null,
                            2,
                        ),
                        uri: 'getStockPriceHistory',
                    },
                ],
            };
        } catch (error) {
            return {
                content: [
                    {
                        type: 'text',
                        text: `Error: ${error instanceof Error ? error.message : 'Failed to get price history'}`,
                        uri: 'getStockPriceHistory',
                    },
                ],
                isError: true,
            };
        }
    };
};
